/**
 * useStashStore — holds canvas elements that have been removed from the
 * visible canvas but kept for potential restoration.
 *
 * ARCHITECTURE (v5.2 — Canvas Visibility Restructure):
 *   When any mechanism wants to "hide" an element from the canvas, it goes
 *   through `canvasController.stash(ids, reason, by)`. The controller:
 *     1. Pulls the element (+ orphan edges) out of `useGraphStore.elements[]`
 *     2. Writes an entry into THIS store, tagged with the stash REASON
 *
 *   The REASON is critical: it controls when the element can be restored.
 *   A filter cleared → only `reason='filter'` items come back. User clicks
 *   eye-on → only `reason='user-hide'` items for that id come back. Etc.
 *
 *   Rendering: the StashTray UI reads this store grouped by reason.
 *   Persistence: via `exportForSave()`, stashed items travel with the
 *   investigation snapshot so they re-appear on reload.
 *
 *   NOTE (Phase 0): this store is provisioned but not yet written by
 *   any mechanism. It will become live starting in Phase 1.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { CytoscapeElement } from '../types/graph';
import { createQuotaSafeSessionStorage } from '../utils/quotaSafeStorage';

/**
 * Why was this element stashed?
 *
 * The reason determines the restore policy:
 *   - user-hide   → sticky. Only eye-on or user restore clears it.
 *                   Survives filter changes and algorithm reruns.
 *   - filter      → auto-restored when the filter relaxes / clears.
 *   - algorithm   → auto-restored when the algorithm reruns or ends.
 *   - manual-stash → user explicitly sent to tray. Only user restores.
 *   - orphan      → edge whose endpoint was stashed. Follows endpoint.
 */
export type StashReason =
    | 'user-hide'
    | 'filter'
    | 'algorithm'
    | 'manual-stash'
    | 'orphan';

/** Who initiated the stash (for telemetry / UI grouping). */
export type StashInitiator = 'user' | 'filter' | 'algorithm';

export interface StashedElement {
    /** Full element (node or edge) ready to be re-added to the graph */
    element: CytoscapeElement;
    /** 'nodes' or 'edges' — cached so UI doesn't need to re-inspect */
    group: 'nodes' | 'edges';
    /** Why it was stashed (see StashReason). */
    reason: StashReason;
    /** Who initiated (user, filter, algorithm). */
    stashedBy: StashInitiator;
    /** Epoch ms */
    stashedAt: number;
    /**
     * For NODE entries: IDs of edges that were auto-stashed as orphans
     * alongside this node. When the node restores, these edges are also
     * restored (subject to endpoint being on canvas too).
     */
    connectedEdgeIds?: string[];
    /**
     * For ORPHAN edge entries: the node id whose stashing orphaned this edge.
     * Used to auto-restore orphans when the owning node restores.
     */
    orphanOf?: string;
}

/**
 * Minimum payload stored in the stash. Keyed by element id so lookups
 * are O(1) and the same id cannot be double-stashed (latest write wins).
 */
export interface StashState {
    entries: Record<string, StashedElement>;

    /** Insert or overwrite a stash entry. */
    setEntry: (id: string, entry: StashedElement) => void;

    /** Bulk insert/overwrite — preferred for filter / algorithm mass stashes. */
    setEntries: (entries: Array<{ id: string; entry: StashedElement }>) => void;

    /** Remove one entry by id (after it's restored to canvas). */
    removeEntry: (id: string) => void;

    /** Remove many entries at once. */
    removeEntries: (ids: string[]) => void;

    /** Drop every entry (e.g. clear canvas / new investigation). */
    clearAll: () => void;

    /** Read helper: does this id live in the stash? */
    hasEntry: (id: string) => boolean;

    /** Read helper: all entries matching a reason. */
    getByReason: (reason: StashReason) => StashedElement[];

    /** Read helper: all entries as a flat array. */
    getAll: () => StashedElement[];

    /**
     * Rehydrate from a saved investigation snapshot.
     * Accepts the same shape as `exportForSave()` returns.
     */
    hydrate: (entries: Record<string, StashedElement>) => void;

    /** Serialize for saved-investigation payload. */
    exportForSave: () => Record<string, StashedElement>;
}

export const useStashStore = create<StashState>()(
    persist(
        (set, get) => ({
            entries: {},

            setEntry: (id, entry) => {
                set(state => ({ entries: { ...state.entries, [id]: entry } }));
            },

            setEntries: (items) => {
                if (items.length === 0) return;
                set(state => {
                    const next = { ...state.entries };
                    for (const { id, entry } of items) {
                        next[id] = entry;
                    }
                    return { entries: next };
                });
            },

            removeEntry: (id) => {
                set(state => {
                    if (!(id in state.entries)) return state;
                    const next = { ...state.entries };
                    delete next[id];
                    return { entries: next };
                });
            },

            removeEntries: (ids) => {
                if (ids.length === 0) return;
                set(state => {
                    let changed = false;
                    const next = { ...state.entries };
                    for (const id of ids) {
                        if (id in next) {
                            delete next[id];
                            changed = true;
                        }
                    }
                    return changed ? { entries: next } : state;
                });
            },

            clearAll: () => set({ entries: {} }),

            hasEntry: (id) => id in get().entries,

            getByReason: (reason) =>
                Object.values(get().entries).filter(e => e.reason === reason),

            getAll: () => Object.values(get().entries),

            hydrate: (entries) => set({ entries: { ...entries } }),

            exportForSave: () => ({ ...get().entries }),
        }),
        {
            name: 'workbench-stash',
            storage: createJSONStorage(() => createQuotaSafeSessionStorage({
                warnKey: '__workbench_stash_quota_warned__',
                warnMessage:
                    '[useStashStore] sessionStorage quota exceeded — stashed nodes will not persist across reloads.',
            })),
            version: 1,
            partialize: (state) => ({ entries: state.entries }) as any,
        },
    ),
);

// Expose store on window for E2E test introspection. Mirrors the
// `window.__cy`, `__useGraphStore`, `__useSelectionStore`, `__useFilterStore`.
if (typeof window !== 'undefined') {
    (window as any).__useStashStore = useStashStore;
}
